import '../../styles/global.css'

import AccountCircle from '@mui/icons-material/AccountCircle'
import {
  Avatar,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material'
import React, { useContext, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes, User } from '../../models'
import { FeatureFlags } from '../../models/FeatureFlags'
import { AuthContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { ColorPalette } from '../../styles'
import {
  getUsernameFromDummyEmail,
  isDummyEmail
} from '../../utils/checkDummyEmail/checkDummyEmail'
import { ClockInButton, ClockOutButton } from '../TimeClock/ClockInOutButton'
import { SwitchShopDialog } from './SwitchShopDialog'
import { SwitchUserDialog } from './SwitchUserDialog'
import { TimeClockBadgeWrapper } from './TimeClockBadgeWrapper'

interface Props {
  currentUser: User
}

export const UserMenu: React.FC<Props> = ({ currentUser }) => {
  const { logOut } = useContext(AuthContext)
  const { shopId } = useAccessTokenAndShopId()

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [switchOpen, setSwitchOpen] = useState(false)
  const [switchShopOpen, setSwitchShopOpen] = useState(false)
  const {
    openTimeClockWithRelated,
    featureFlags,
    canAccessExpenseCard,
    shopProfile
  } = useContext(AppContext)
  const enableInspections =
    featureFlags && featureFlags[FeatureFlags.DigitalInspection]

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleCloseDialog = () => {
    setAnchorEl(null)
    setSwitchOpen(false)
  }
  const handleSwitchShopCloseDialog = () => {
    setAnchorEl(null)
    setSwitchShopOpen(false)
  }

  const intakeBrochureUrl =
    'print.html?' + new URLSearchParams({ type: 'intake', shopId })

  return (
    <>
      <Dialog open={switchOpen} onClose={handleCloseDialog}>
        <DialogTitle>Switch User</DialogTitle>
        <SwitchUserDialog handleCloseDialog={handleCloseDialog} />
      </Dialog>

      <Dialog open={switchShopOpen} onClose={handleSwitchShopCloseDialog}>
        <DialogTitle>Switch Shop</DialogTitle>
        <SwitchShopDialog handleCloseDialog={handleSwitchShopCloseDialog} />
      </Dialog>

      <IconButton
        size="large"
        aria-label="User Menu"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit">
        <TimeClockBadgeWrapper>
          {currentUser.profilePicUrl ? (
            <Avatar
              test-id="menuAvatarPicture"
              alt={currentUser.name}
              src={currentUser.profilePicUrl}
            />
          ) : (
            <AccountCircle
              test-id="menuAvatarDefault"
              color="action"
              fontSize="large"
            />
          )}
        </TimeClockBadgeWrapper>
      </IconButton>

      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}>
        <MenuItem
          sx={{
            whiteSpace: 'normal',
            pointerEvents: 'none',
            color: ColorPalette.PRIMARY
          }}>
          {currentUser.name} <br />
          {isDummyEmail(currentUser.email || '')
            ? getUsernameFromDummyEmail(currentUser.email || '')
            : currentUser.email}
        </MenuItem>
        <Divider />
        {featureFlags && featureFlags[FeatureFlags.TimeClock] && (
          <MenuItem disableRipple>
            {openTimeClockWithRelated ? <ClockOutButton /> : <ClockInButton />}
          </MenuItem>
        )}
        {featureFlags && featureFlags[FeatureFlags.TimeClock] && <Divider />}
        <MenuItem
          component={Link}
          to={AppRoutes.SETTINGS_PERSONAL}
          onClick={handleClose}
          data-testid="userProfileButton">
          My Profile
        </MenuItem>
        {enableInspections && (
          <MenuItem
            component={Link}
            to={AppRoutes.DVI_HOME}
            onClick={handleClose}>
            DVI
          </MenuItem>
        )}
        <MenuItem
          component={Link}
          to={generatePath(AppRoutes.INTAKE, {
            shopId: shopProfile!.id!
          })}>
          Registration Form
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose()
            window.open(intakeBrochureUrl)
          }}>
          Registration Brochure
        </MenuItem>
        {featureFlags && featureFlags[FeatureFlags.AppointmentSchedule] && (
          <MenuItem
            component={Link}
            to={generatePath(AppRoutes.APPOINTMENT_SCHEDULE, {
              shopId
            })}>
            Appointment Schedule
          </MenuItem>
        )}
        {canAccessExpenseCard && (
          <MenuItem
            component={Link}
            to={AppRoutes.EXPENSE_CARD}
            onClick={handleClose}>
            Corporate Card
          </MenuItem>
        )}
        <MenuItem onClick={() => setSwitchOpen(true)}>Switch User</MenuItem>
        {/* Only show the switch shop option if the user has more than one shop */}
        {currentUser.roles && currentUser.roles.length > 1 && (
          <MenuItem onClick={() => setSwitchShopOpen(true)}>
            Switch Shop
          </MenuItem>
        )}
        <MenuItem onClick={logOut}>Sign Out</MenuItem>
      </Menu>
    </>
  )
}
